import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { saveAndReplaceToken } from "../../../lib/axiosAuth";
import { API } from "./constants";

/**
 * Centralises every per-row mutation the superadmin can perform on a user.
 * Inline-edit state (editingRole, editingOrg) is also tracked here so the
 * Users page is purely declarative.
 *
 * The `onMutated` callback is invoked after any successful action — the
 * caller wires it to fetchUsers() to refresh the table.
 */
export function useUserActions({ onMutated }) {
  const [impersonating, setImpersonating] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [editingOrg, setEditingOrg] = useState(null);
  const [newOrgId, setNewOrgId] = useState("");
  const [deletingUser, setDeletingUser] = useState(null);
  const [togglingSA, setTogglingSA] = useState(null);
  // Reset-password modal: holds the target user when open, null when
  // closed. `resetting` is the inflight flag so the confirm button
  // can show its loading state independently of the modal state.
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  const handleImpersonate = async (userId) => {
    setImpersonating(userId);
    try {
      const res = await axios.post(
        `${API}/superadmin/impersonate`,
        { user_id: userId },
        { withCredentials: true },
      );
      saveAndReplaceToken(res.data.impersonation_token);
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al impersonar");
      setImpersonating(null);
    }
  };

  const handleEditRole = (userId, currentRole) => {
    setEditingRole(userId);
    setNewRole(currentRole);
  };
  const handleSaveRole = async (userId) => {
    try {
      await axios.put(
        `${API}/superadmin/users/${userId}/role`,
        { role: newRole },
        { withCredentials: true },
      );
      toast.success("Rol actualizado exitosamente");
      setEditingRole(null);
      onMutated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar rol");
    }
  };
  const handleCancelEdit = () => {
    setEditingRole(null);
    setNewRole("");
  };

  const handleEditOrg = (userId, currentOrgId) => {
    setEditingOrg(userId);
    setNewOrgId(currentOrgId);
  };
  const handleSaveOrg = async (userId) => {
    try {
      await axios.put(
        `${API}/superadmin/users/${userId}/organization`,
        { organization_id: newOrgId },
        { withCredentials: true },
      );
      toast.success("Organización actualizada exitosamente");
      setEditingOrg(null);
      onMutated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar organización");
    }
  };
  const handleCancelOrgEdit = () => {
    setEditingOrg(null);
    setNewOrgId("");
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`¿Estás seguro de eliminar permanentemente al usuario ${userEmail}?\n\nEsta acción NO se puede deshacer.`)) {
      return;
    }
    setDeletingUser(userId);
    try {
      await axios.delete(`${API}/superadmin/users/${userId}`, { withCredentials: true });
      toast.success("Usuario eliminado exitosamente");
      onMutated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar usuario");
    } finally {
      setDeletingUser(null);
    }
  };

  // Promote/demote — only the root superadmin's calls succeed on the backend.
  // The UI hides the action for everyone else, but the server remains the
  // single source of truth.
  const handleToggleSuperadmin = async (user) => {
    const willPromote = user.role !== "superadmin";
    const action = willPromote ? "promover a Superadmin" : "quitar el rol Superadmin";
    const warning = willPromote
      ? "Esto le dará acceso COMPLETO a la plataforma, incluyendo toda organización, todo usuario y todos los datos. Solo concédelo a personas de máxima confianza."
      : `${user.email} dejará de tener acceso al panel de superadmin y volverá al rol "owner" en su organización (si la tenía).`;

    if (!window.confirm(`¿Confirmas ${action} a ${user.email}?\n\n${warning}`)) return;

    setTogglingSA(user.user_id);
    try {
      const res = await axios.put(
        `${API}/superadmin/users/${user.user_id}/superadmin`,
        { is_superadmin: willPromote, fallback_role: "owner" },
        { withCredentials: true },
      );
      toast.success(res.data?.message || "Privilegio actualizado");
      onMutated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar privilegio");
    } finally {
      setTogglingSA(null);
    }
  };

  // Open the reset-password confirmation modal. The actual POST happens
  // after the SA submits the modal; this just stashes the target.
  const handleResetPassword = (user) => {
    setResetPasswordTarget(user);
  };
  const handleConfirmResetPassword = async ({ reason }) => {
    if (!resetPasswordTarget) return;
    setResetting(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/users/${resetPasswordTarget.user_id}/password-reset`,
        { reason: reason || null },
        { withCredentials: true },
      );
      // Success messaging mirrors the backend's email_sent flag so the
      // SA gets clear feedback when Brevo failed and a retry is needed.
      const msg = res.data?.message
        || `Email enviado a ${resetPasswordTarget.email}`;
      if (res.data?.email_sent === false) {
        toast.warning(msg);
      } else {
        toast.success(msg);
      }
      setResetPasswordTarget(null);
      // No `onMutated` call: the user list doesn't visually change from
      // a password reset, only an audit-log row was created.
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar el reseteo");
    } finally {
      setResetting(false);
    }
  };
  const handleCancelResetPassword = () => {
    if (resetting) return;
    setResetPasswordTarget(null);
  };

  return {
    impersonating, editingRole, newRole, setNewRole,
    editingOrg, newOrgId, setNewOrgId,
    deletingUser, togglingSA,
    resetPasswordTarget, resetting,
    handleImpersonate,
    handleEditRole, handleSaveRole, handleCancelEdit,
    handleEditOrg, handleSaveOrg, handleCancelOrgEdit,
    handleDeleteUser, handleToggleSuperadmin,
    handleResetPassword, handleConfirmResetPassword, handleCancelResetPassword,
  };
}
